import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Expense from '../../entities/expense';
import Matter from '../../entities/matter';
import Invoice from '../../entities/invoice';
import Document from '../../entities/document';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
export function canDeleteExpense(): boolean {
return canCascadeDelete();
}
export function collectExpenseDeleteImpact(expense: Expense, context: DeleteImpactContext): void {
const key = 'expense:' + expense.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
if (!!expense.invoiceId) {
addDeleteImpact(context, key, 'expenses*');
return;
}
const matterExpensesImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.expenses?.includes(expense.id));
matterExpensesImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters'));
const invoiceExpenseIdsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.expenseIds?.includes(expense.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceExpenseIdsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'expense' && document.entityId === expense.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
}
export function deleteImpactExpense(expense: Expense): DeleteImpact {
const context = deleteImpactContext();
collectExpenseDeleteImpact(expense, context);
return deleteImpactCounts(context);
}
export function deleteExpense(expense: Expense, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (cascade && (!!expense.invoiceId)) return {status: 'blocked', impacts: [{value: 'expenses*', count: 1}]};
if (!cascade) {
if (!canDeleteExpense()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectExpenseDeleteImpact(expense, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || expense.id};
}
const key = 'expense:' + expense.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'expense' && document.entityId === expense.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('expenseId', expense.id)]).filter(matterTemp => matterTemp.expenses?.includes(expense.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.expenses = _matter.expenses?.filter(id => id !== expense.id) || [];

_work.set('matter:' + _matter.id, _matter);
dispatch(Actions.MatterActions.saveMatterQueue(_matter), 'matter:' + _matter.id);
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('expenseIds', expense.id)]).filter(invoiceTemp => invoiceTemp.expenseIds?.includes(expense.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.expenseIds = _invoice.expenseIds?.filter(id => id !== expense.id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});
const deleted = expense.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.ExpenseActions.removeExpenseQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceExpense(expense: Expense, replaceWith: Expense, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = expense.id;}
const _work = workingSet();
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('expenseId', expense.id)]).filter(matterTemp => matterTemp.expenses?.includes(expense.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.expenses = _matter.expenses?.map(id => (id === expense.id) ? replaceWith.id : id) || [];

_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('expenseIds', expense.id)]).filter(invoiceTemp => invoiceTemp.expenseIds?.includes(expense.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.expenseIds = _invoice.expenseIds?.map(id => (id === expense.id) ? replaceWith.id : id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});



workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'expense' && document.entityId === expense.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
return expense;
}
